import React, { useState, useCallback } from 'react'

import { BaseView, BaseText } from '@/components/base'
import { TransactionsList } from '@/components/TransactionsList'
import { RequestsList } from '@/components/RequestsList'
import { ActivityFilters } from '@/components/ActivityFilters'
import { EmptyActivity, EmptyTransaction } from '@/components/EmptyStates'
import { TransactionTypes, RangeDate } from '@/domain/transactions'
import { useActivityData } from '@/hooks/useActivityData'
import { useTranslation } from 'react-i18next'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { PullToRefresh } from '@/components/PullToRefresh'
import { ActivityCashbackBanner } from '@/components/ActivityCashbackBanner'
import { useGetWallet } from '@/domain'
import { usePullToRefreshHandler } from '@/hooks'

export const ActivityScreen: React.FC = () => {
  const { t } = useTranslation()

  // Filter states
  const [searchValue, setSearchValue] = useState('')
  const [selectedTypes, setSelectedTypes] = useState<TransactionTypes[]>([])
  const [dateRange, setDateRange] = useState<RangeDate>({
    startDate: null,
    endDate: null,
  })

  // Fetch activity data (transactions + requests)
  const {
    transactions: allTransactions,
    requests: allRequests,
    transactionsQuery,
    requestsQuery,
    isLoading,
    isFetched,
    isError,
    bothAreEmpty,
    refetch,
  } = useActivityData({
    searchValue,
    selectedTypes,
    dateRange,
    showRequests: true, // Enable requests display as in React Native
  })

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = transactionsQuery
  const { displayCurrency } = useAppPreferencesStore()

  // Handlers
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Request handlers
  const handleDeclineRequest = useCallback(async () => {
    // Request actions are now handled by RequestItem component using useRequestItemHandler
    // This callback is called after successful action to refresh the list
    requestsQuery.refetch()
  }, [requestsQuery])

  const handleAcceptRequest = useCallback(async () => {
    // Request actions are now handled by RequestItem component using useRequestItemHandler
    // This callback is called after successful action to refresh both requests and transactions
    refetch()
  }, [refetch])

  const { isRefreshing, handlePullRefresh } = usePullToRefreshHandler(refetch, {
    showErrorToast: true,
  })

  // Get wallet data - must be called before any conditional returns
  const { data: walletData, isLoading: isLoadingWallet } = useGetWallet()
  const hasCards = isLoadingWallet
    ? true // Hide banner while loading
    : Boolean(walletData?.cards?.length && walletData?.cards?.length > 0)

  // Check if this is a completely empty state (no filters, no search, no transactions, no requests)
  // Only show empty state if data is fetched, not loading and no error occurred
  const isCompletelyEmpty =
    bothAreEmpty &&
    isFetched &&
    !isLoading &&
    !isError &&
    !searchValue &&
    selectedTypes.length === 0 &&
    !dateRange.startDate &&
    !dateRange.endDate

  // Show empty activity state when there are no transactions and no filters - matches RN logic
  if (isCompletelyEmpty) {
    return <EmptyActivity />
  }

  return (
    <PullToRefresh onRefresh={handlePullRefresh} isRefreshing={isRefreshing}>
      {/* Sticky Header with Telegram Offsets */}

      <BaseView data-component="header-navigation-activity" px={12} py={20}>
        <BaseText fontSize={24} fontFamily="semibold" color="label.default">
          {t('translation:activity')}
        </BaseText>

        {/* Filters */}
        <ActivityFilters
          onSearchChange={setSearchValue}
          selectedTypes={selectedTypes}
          onTypesChange={setSelectedTypes}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      </BaseView>

      {/* Scrollable Content Area */}
      <BaseView
        style={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <div
          className="content-scroll"
          style={{
            height: '100%',
            overflow: 'auto',
            paddingBottom: '100px',
          }}
        >
          {/* Requests List - as in React Native */}
          {allRequests.length > 0 && (
            <RequestsList
              requests={allRequests}
              onDeclineRequest={handleDeclineRequest}
              onAcceptRequest={handleAcceptRequest}
              displayCurrency={displayCurrency}
            />
          )}
          <BaseView px={12}>{hasCards && <ActivityCashbackBanner />}</BaseView>

          {/* Transactions List or Loading/Error State */}
          {allTransactions.length === 0 && isLoading ? (
            <BaseView px={12}>
              <EmptyTransaction isLoadingTransactions={true} />
            </BaseView>
          ) : allTransactions.length === 0 && isError && !isLoading ? (
            <BaseView center py={40} px={20}>
              <BaseText
                fontSize={14}
                fontFamily="regular"
                color="label.default"
                textAlign="center"
                style={{ opacity: 0.7 }}
              >
                {t('translation:transactionsNotFound') ||
                  "Sorry, we couldn't find any transactions"}
              </BaseText>
            </BaseView>
          ) : (
            <BaseView px={12}>
              <TransactionsList
                transactions={allTransactions}
                isLoading={isLoading && allTransactions.length === 0}
                onLoadMore={handleLoadMore}
                hasNextPage={hasNextPage}
                isLoadingMore={isFetchingNextPage}
              />
            </BaseView>
          )}
        </div>
      </BaseView>
    </PullToRefresh>
  )
}
